import { ISHIKAWA_CATEGORIES } from '../data/ishikawaCategories';

export interface IshikawaCause {
  cause: string;
  explanation: string;
}

/** One category's worth of causes, ready to draw. */
export interface IshikawaBone {
  category: string;
  colour: string;
  top: boolean;
  causes: IshikawaCause[];
}

/**
 * Keys the payload may be wrapped in. The Arcola column holds the WHOLE RCA
 * response — `{ Ishikawa, FaultTree, EventTree }` — so the diagram data is
 * one level down; older tooling wrote the category map at the top level.
 */
const WRAPPER_KEYS = ['Ishikawa', 'ishikawa', 'ishikawa_diagram', 'ishikawaDiagram'];

const CAUSE_KEYS = ['Cause', 'cause', 'name', 'title', 'issue'];
const EXPLANATION_KEYS = ['Explanation', 'explanation', 'description', 'desc', 'detail'];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Filler the LLM writes to pad a category to its quota ("N/A" / "N/A" on
 * about two records in five) — no cause at all, so it must not draw a tick.
 * Same set the Webflow sync's `str()` blanks.
 */
function isPlaceholder(s: string): boolean {
  return /^(n\/?a|none|null|undefined)\.?$/i.test(s.trim());
}

/** First non-empty, non-placeholder string among `keys`, or ''. */
function pick(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim() !== '' && !isPlaceholder(v)) return v.trim();
  }
  return '';
}

/** Does this object look like the six-category map rather than a wrapper? */
function looksLikeCategoryMap(obj: Record<string, unknown>): boolean {
  return ISHIKAWA_CATEGORIES.some((c) => Array.isArray(obj[c.key]));
}

function unwrap(root: Record<string, unknown>): Record<string, unknown> | null {
  for (const k of WRAPPER_KEYS) {
    const v = root[k];
    if (isRecord(v)) return v;
  }
  if (looksLikeCategoryMap(root)) return root;
  // One more level down: some writers nest the whole RCA under a single key.
  for (const v of Object.values(root)) {
    if (isRecord(v) && looksLikeCategoryMap(v)) return v;
  }
  return null;
}

function normaliseCauses(raw: unknown): IshikawaCause[] {
  if (!Array.isArray(raw)) return [];
  const out: IshikawaCause[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string') {
      const cause = entry.trim();
      if (cause && !isPlaceholder(cause)) out.push({ cause, explanation: '' });
      continue;
    }
    if (!isRecord(entry)) continue;
    const cause = pick(entry, CAUSE_KEYS);
    const explanation = pick(entry, EXPLANATION_KEYS);
    // A bare explanation with no cause still carries information — promote it
    // rather than dropping the row.
    if (cause) out.push({ cause, explanation });
    else if (explanation) out.push({ cause: explanation, explanation: '' });
  }
  return out;
}

/**
 * Turn whatever is stored in the `ishikawa` field into bones ready to draw.
 *
 * Accepts a JSON string (how Airtable long-text round-trips it) or an
 * already-parsed object, the full RCA response or a bare category map, and
 * either capitalisation of the Cause/Explanation keys — the same tolerance
 * the Python `generate_ishikawa_from_rca` had, for the same reason: this
 * data is LLM-written and the shape has drifted between schema versions.
 *
 * Returns [] for anything unusable, including empty categories, so callers
 * can treat "no bones" as "render nothing".
 */
export function parseIshikawa(raw: unknown): IshikawaBone[] {
  let root: unknown = raw;

  if (typeof root === 'string') {
    const text = root.trim();
    if (!text) return [];
    try {
      root = JSON.parse(text);
    } catch {
      return [];
    }
  }

  if (!isRecord(root)) return [];

  const categories = unwrap(root);
  if (!categories) return [];

  const bones: IshikawaBone[] = [];
  for (const { key, colour, top } of ISHIKAWA_CATEGORIES) {
    const causes = normaliseCauses(categories[key]);
    if (causes.length > 0) bones.push({ category: key, colour, top, causes });
  }
  return bones;
}
